import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Modal, Select, Table } from 'antd';
import dayjs from 'dayjs';
import { Plus, Search } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { fmtMoney, isWorking, normText, toDay } from './hrLogic';
import { fmtMonth, monthOf } from './tpLogic';
import { ADJ_KIND, isPeriodLocked, type AdjKind, type Adjust } from './payLogic';
import { Badge, Chip, EmpCell, Field, Kpi, PageHeader, fmtDateTime } from './hrUi';
import { MoneyInput } from './hrModals';
import { MonthNav, done, useTpPerms } from './tpUi';

type Draft = Omit<Adjust, 'id' | 'by' | 'at'> & { id?: string };

/** Tạm ứng, thưởng, hoa hồng, truy lĩnh, khấu trừ khác theo kỳ lương */
export default function PayAdjust() {
  const s = useHr();
  const t = useTp();
  const { canPayEdit } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => { const n = new URLSearchParams(params); Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k))); setParams(n, { replace: true }); };
  const month = params.get('m') || monthOf(toDay());
  const kind = (params.get('kind') || '') as AdjKind | '';
  const q = params.get('q') || '';
  const [edit, setEdit] = useState<Draft | null>(null);
  const locked = isPeriodLocked(t, month);
  const inMonth = t.adjustments.filter(a => a.month === month);
  const list = inMonth.filter(a => (!kind || a.kind === kind) && (!q || normText(`${s.employees.find(e => e.id === a.empId)?.name} ${a.note}`).includes(normText(q))))
    .sort((a, b) => b.at - a.at);
  const sum = (k: AdjKind) => inMonth.filter(a => a.kind === k).reduce((n, a) => n + a.amount, 0);
  const emp = (id: string) => s.employees.find(e => e.id === id);

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Tạm ứng & điều chỉnh lương" desc="Các khoản cộng / trừ vào kỳ lương — tự đưa vào bảng lương khi tính; kỳ đã gửi duyệt thì khóa"
          extra={canPayEdit && <Button type="primary" icon={<Plus size={14} />} disabled={locked} onClick={() => setEdit({ empId: '', kind: 'advance', month, amount: 0, note: '' })}>Thêm khoản</Button>} />
        <div className="flex flex-wrap items-center gap-2">
          <MonthNav month={month} onChange={m => go({ m: m === monthOf(toDay()) ? null : m })} />
          <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, nội dung" className="!w-[200px]" value={q} onChange={e => go({ q: e.target.value || null })} />
          {locked && <Badge className="bg-sky-50 text-sky-700 border-sky-200">Kỳ {fmtMonth(month)} đã gửi duyệt — thêm vào kỳ sau</Badge>}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {(Object.keys(ADJ_KIND) as AdjKind[]).map(k => (
            <Kpi key={k} icon={<span className="text-sm font-bold">{ADJ_KIND[k].sign > 0 ? '+' : '−'}</span>} tone={ADJ_KIND[k].badge.split(' ').slice(0, 2).join(' ')}
              label={ADJ_KIND[k].label} value={fmtMoney(sum(k))} hint={`${inMonth.filter(a => a.kind === k).length} khoản`} onClick={() => go({ kind: kind === k ? null : k })} active={kind === k} />
          ))}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Chip active={!kind} onClick={() => go({ kind: null })}>Tất cả · {inMonth.length}</Chip>
          {(Object.keys(ADJ_KIND) as AdjKind[]).map(k => <Chip key={k} active={kind === k} onClick={() => go({ kind: k })}>{ADJ_KIND[k].label}</Chip>)}
        </div>
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">
          <Table size="middle" rowKey="id" dataSource={list} scroll={{ x: 900 }} pagination={{ pageSize: 25, showSizeChanger: false }}
            onRow={a => ({ onClick: () => canPayEdit && !locked && setEdit({ ...a }), className: canPayEdit && !locked ? 'cursor-pointer' : '' })}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`Chưa có khoản nào trong ${fmtMonth(month)}`} /> }}
            columns={[
              { title: 'Nhân sự', key: 'e', width: 250, render: (_, a) => <EmpCell s={s} e={emp(a.empId)} /> },
              { title: 'Loại', key: 'k', width: 170, render: (_, a) => <Badge className={ADJ_KIND[a.kind].badge}>{ADJ_KIND[a.kind].label}</Badge> },
              { title: 'Số tiền', key: 'amt', width: 140, align: 'right', render: (_, a) => <b className={ADJ_KIND[a.kind].sign < 0 ? 'text-rose-600' : 'text-emerald-600'}>{ADJ_KIND[a.kind].sign < 0 ? '−' : '+'}{fmtMoney(a.amount)}</b> },
              { title: 'Nội dung', key: 'n', width: 240, render: (_, a) => a.note },
              { title: 'Ghi nhận', key: 'by', width: 170, render: (_, a) => <span className="text-xs text-gray-500">{a.by} · {fmtDateTime(a.at)}</span> },
            ]} />
        </div>
      </div>
      {edit && <AdjModal d={edit} onClose={() => setEdit(null)} />}
    </Layout>
  );
}

function AdjModal({ d: init, onClose }: { d: Draft; onClose: () => void }) {
  const s = useHr();
  const { me } = useTpPerms();
  const [d, setD] = useState(init);
  const patch = (p: Partial<Draft>) => setD(x => ({ ...x, ...p }));
  const monthStart = `${d.month}-01`;
  const emps = s.employees.filter(e => isWorking(e, monthStart) || isWorking(e, dayjs(monthStart).endOf('month').format('YYYY-MM-DD')) || e.id === d.empId);
  return (
    <Modal open title={d.id ? 'Sửa khoản điều chỉnh' : 'Thêm khoản điều chỉnh'} onCancel={onClose}
      footer={[
        d.id && <Button key="del" danger onClick={() => { if (done(tpApi.removeAdjust(d.id!, me), 'Đã xóa')) onClose(); }}>Xóa</Button>,
        <Button key="c" onClick={onClose}>Thôi</Button>,
        <Button key="s" type="primary" onClick={() => { if (done(tpApi.saveAdjust(d, me), 'Đã lưu')) onClose(); }}>Lưu</Button>,
      ]}>
      <div className="space-y-3 text-sm">
        <Field label="Nhân sự">
          <Select className="w-full" showSearch optionFilterProp="label" value={d.empId || undefined} placeholder="Chọn nhân sự" onChange={empId => patch({ empId })}
            options={emps.map(e => ({ value: e.id, label: `${e.name} · ${e.code}` }))} />
        </Field>
        <Field label="Loại" hint={ADJ_KIND[d.kind].hint}>
          <Select className="w-full" value={d.kind} onChange={(kind: AdjKind) => patch({ kind })} options={(Object.keys(ADJ_KIND) as AdjKind[]).map(k => ({ value: k, label: ADJ_KIND[k].label }))} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Số tiền"><MoneyInput value={d.amount || undefined} onChange={amount => patch({ amount: amount || 0 })} /></Field>
          <Field label="Tính vào kỳ lương"><Input value={fmtMonth(d.month)} disabled /></Field>
        </div>
        <Field label="Nội dung"><Input.TextArea rows={2} value={d.note} onChange={e => patch({ note: e.target.value })} /></Field>
      </div>
    </Modal>
  );
}
